import { RAW_ENDPOINTS, UPLOAD_METADATA_KEYS } from '@seans/contracts';
import type { ApiClient } from '../../../shared/api/client.ts';
import { startTusUpload, type TusUploadHandle } from '../../../shared/api/tus.ts';
import { createStore, type ReadableStore } from '../../../shared/lib/store.ts';
import { fileMatches, type ResumeRecord, uploadRecords } from './uploadRecords.ts';

export type UploadPhase = 'uploading' | 'paused' | 'failed';

export interface UploadEntry {
  readonly assetId: string;
  readonly title: string;
  readonly filename: string;
  readonly bytesSent: number;
  readonly bytesTotal: number;
  readonly phase: UploadPhase;
}

export class UploadFileMismatchError extends Error {
  constructor(readonly expectedFilename: string) {
    super(`Expected ${expectedFilename}`);
    this.name = 'UploadFileMismatchError';
  }
}

export interface UploadManager {
  /** Uploads running (or paused/failed) in this tab, by asset id. */
  readonly entries: ReadableStore<ReadonlyMap<string, UploadEntry>>;
  /** Creates the film and starts sending the file; resolves with the new asset id. */
  start(file: File, title: string): Promise<string>;
  /** Continues an upload interrupted by a reload; the host picks the same file again. */
  resume(assetId: string, title: string, file: File): Promise<void>;
  pause(assetId: string): Promise<void>;
  proceed(assetId: string): void;
  /** Stops the upload and deletes the film it belonged to. */
  cancel(assetId: string): Promise<void>;
  /** The original file name of an interrupted upload, if this browser started it. */
  expectedFilename(assetId: string): string | null;
  /**
   * Forgets resume records of listed films that no longer wait for their file
   * (`known`: asset id → still awaiting its upload). Films not listed are left alone.
   */
  reconcile(known: ReadonlyMap<string, boolean>): void;
}

export interface UploadManagerDeps {
  readonly api: ApiClient;
  /** Called whenever the server-side state of films changed (created, uploaded, cancelled). */
  readonly onAssetsChanged: () => void;
}

export const createUploadManager = ({ api, onAssetsChanged }: UploadManagerDeps): UploadManager => {
  const entries = createStore<ReadonlyMap<string, UploadEntry>>(new Map());
  const handles = new Map<string, TusUploadHandle>();

  const update = (assetId: string, patch: Partial<UploadEntry>) =>
    entries.set((current) => {
      const entry = current.get(assetId);
      if (!entry) return current;
      return new Map(current).set(assetId, { ...entry, ...patch });
    });

  const drop = (assetId: string) =>
    entries.set((current) => {
      if (!current.has(assetId)) return current;
      const next = new Map(current);
      next.delete(assetId);
      return next;
    });

  // Leaving the page mid-upload loses the File handle; the browser asks before that happens.
  window.addEventListener('beforeunload', (event) => {
    for (const entry of entries.get().values()) {
      if (entry.phase === 'uploading') {
        event.preventDefault();
        return;
      }
    }
  });

  const send = async (record: ResumeRecord, file: File) => {
    entries.set((current) =>
      new Map(current).set(record.assetId, {
        assetId: record.assetId,
        title: record.title,
        filename: file.name,
        bytesSent: 0,
        bytesTotal: file.size,
        phase: 'uploading',
      }),
    );
    const handle = await startTusUpload({
      file,
      endpoint: record.endpoint,
      metadata: record.metadata,
      onProgress: (bytesSent, bytesTotal) =>
        update(record.assetId, { bytesSent, bytesTotal, phase: 'uploading' }),
      onSuccess: () => {
        handles.delete(record.assetId);
        uploadRecords.remove(record.assetId);
        drop(record.assetId);
        onAssetsChanged();
      },
      onError: (error) => {
        console.error('[uploads] upload failed', record.assetId, error);
        update(record.assetId, { phase: 'failed' });
      },
    });
    handles.set(record.assetId, handle);
  };

  return {
    entries,
    async start(file, title) {
      const created = await api.call('createAsset', {
        body: { title, filename: file.name, sizeBytes: file.size },
      });
      const record: ResumeRecord = {
        assetId: created.asset.id,
        title: created.asset.title,
        filename: file.name,
        sizeBytes: file.size,
        lastModified: file.lastModified,
        endpoint: created.upload.endpoint,
        metadata: created.upload.metadata,
      };
      uploadRecords.put(record);
      onAssetsChanged();
      await send(record, file);
      return record.assetId;
    },
    async resume(assetId, title, file) {
      const stored = uploadRecords.get(assetId);
      if (stored && !fileMatches(stored, file)) throw new UploadFileMismatchError(stored.filename);
      // Without a record (another browser started it) the contract tells us how to address the asset.
      const record: ResumeRecord = stored ?? {
        assetId,
        title,
        filename: file.name,
        sizeBytes: file.size,
        lastModified: file.lastModified,
        endpoint: RAW_ENDPOINTS.uploads,
        metadata: { [UPLOAD_METADATA_KEYS.assetId]: assetId, [UPLOAD_METADATA_KEYS.filename]: file.name },
      };
      uploadRecords.put(record);
      await send(record, file);
    },
    async pause(assetId) {
      await handles.get(assetId)?.pause();
      update(assetId, { phase: 'paused' });
    },
    proceed(assetId) {
      handles.get(assetId)?.resume();
      update(assetId, { phase: 'uploading' });
    },
    async cancel(assetId) {
      const handle = handles.get(assetId);
      handles.delete(assetId);
      try {
        await handle?.cancel();
      } catch (error) {
        // The film is deleted below either way; a leftover partial upload is cleaned by the server.
        console.warn('[uploads] could not terminate upload', assetId, error);
      }
      await api.call('deleteAsset', { params: { assetId } });
      uploadRecords.remove(assetId);
      drop(assetId);
      onAssetsChanged();
    },
    expectedFilename: (assetId) => uploadRecords.get(assetId)?.filename ?? null,
    reconcile(known) {
      for (const assetId of Object.keys(uploadRecords.all())) {
        if (known.get(assetId) === false && !entries.get().has(assetId)) uploadRecords.remove(assetId);
      }
    },
  };
};
